import {
  formatWaitDuration,
  type WaitOutcome,
  type WaitProgram,
  type WaitStep
} from '../utils/wait-program';

/** Top-to-bottom preview of how the customer will live the wait. */
export function waitProgramPreviewLines(program: WaitProgram): string[] {
  const byId = new Map(program.steps.map((step) => [step.id, step]));
  const lines: string[] = [];
  const seen = new Set<string>();
  // Every reachable step, once each: the otherwise path first, then each branch's target.
  const queue: string[] = [program.start];

  while (queue.length > 0) {
    const id = queue.shift()!;
    if (!byId.has(id) || seen.has(id)) continue;
    seen.add(id);
    const step = byId.get(id)!;
    lines.push(`Wait ${formatWaitDuration(step.afterMinutes)}.`);
    if (step.actions.length > 0) {
      lines.push(`Check ${step.actions.map((a) => a.tool).join(', ')}.`);
    }
    for (const branch of step.when) {
      lines.push(`If ${branch.if.field} ${branch.if.op}${valueSuffix(branch.if.value)}: ${outcomePreview(branch.then)}.`);
    }
    lines.push(
      step.when.length > 0
        ? `Otherwise: ${outcomePreview(step.otherwise)}.`
        : `If there is no reply: ${outcomePreview(step.otherwise)}.`
    );
    queue.push(...nextStepIds(step));
  }

  if (program.expiry) {
    lines.push(
      `Expires after ${formatWaitDuration(program.expiry.afterMinutes)}: ${outcomePreview(program.expiry.then)}.`
    );
  }
  return lines;
}

function valueSuffix(value: unknown): string {
  if (value === undefined) return '';
  if (Array.isArray(value)) return ` ${value.join(', ')}`;
  return ` ${String(value)}`;
}

function outcomePreview(outcome: WaitOutcome): string {
  switch (outcome.do) {
    case 'compose':
      return outcome.next ? `send a follow-up, then continue` : 'send a follow-up';
    case 'run':
      return outcome.actions.length
        ? `run ${outcome.actions.map((a) => a.tool).join(', ')}`
        : 'run ticket actions';
    case 'wait':
      return `wait again (${outcome.step})`;
    case 'handoff':
      return 'hand off';
    case 'end':
      return 'end';
  }
}

function nextStepIds(step: WaitStep): string[] {
  return [step.otherwise, ...step.when.map((branch) => branch.then)]
    .map(outcomeNext)
    .filter((id): id is string => id !== undefined);
}

function outcomeNext(outcome: WaitOutcome): string | undefined {
  if (outcome.do === 'wait') return outcome.step;
  if ((outcome.do === 'compose' || outcome.do === 'run') && outcome.next) return outcome.next;
  return undefined;
}
